import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DENTAL_SPECIALTIES } from '../lib/constants';
import MaterialLedgerSheet from '../components/MaterialLedgerSheet';

// Thai Months Definition
export const THAI_MONTHS = [
  { value: 1, name: 'มกราคม', short: 'ม.ค.' },
  { value: 2, name: 'กุมภาพันธ์', short: 'ก.พ.' },
  { value: 3, name: 'มีนาคม', short: 'มี.ค.' },
  { value: 4, name: 'เมษายน', short: 'เม.ย.' },
  { value: 5, name: 'พฤษภาคม', short: 'พ.ค.' },
  { value: 6, name: 'มิถุนายน', short: 'มิ.ย.' },
  { value: 7, name: 'กรกฎาคม', short: 'ก.ค.' },
  { value: 8, name: 'สิงหาคม', short: 'ส.ค.' },
  { value: 9, name: 'กันยายน', short: 'ก.ย.' },
  { value: 10, name: 'ตุลาคม', short: 'ต.ค.' },
  { value: 11, name: 'พฤศจิกายน', short: 'พ.ย.' },
  { value: 12, name: 'ธันวาคม', short: 'ธ.ค.' }
];

// Helper to resolve specialty from master or item name
export const resolveSpecialty = (rawSpecialty, itemName = '') => {
  if (rawSpecialty) {
    const found = DENTAL_SPECIALTIES.find(
      s => s.id === rawSpecialty || s.name === rawSpecialty || s.code === rawSpecialty
    );
    if (found) return found;
  }

  // Check if itemName contains known specialty keywords
  if (itemName) {
    for (const s of DENTAL_SPECIALTIES) {
      if (s.id !== 'general') {
        const cleanKeyword = s.name.replace(/^งาน/, '').trim();
        if (cleanKeyword && itemName.includes(cleanKeyword)) {
          return s;
        }
      }
    }
  }

  return (
    DENTAL_SPECIALTIES.find(s => s.id === 'general') || {
      id: 'general',
      name: 'ทันตกรรมทั่วไป/อื่นๆ',
      code: 'GEN',
      color: '#4b5563'
    }
  );
};

// Helper to parse order note
export const parseOrderNote = (noteStr) => {
  if (!noteStr) return { orderedBy: 'ไม่ระบุ', noteText: '' };
  if (typeof noteStr === 'string' && noteStr.startsWith('ordered_by:')) {
    const pipeIdx = noteStr.indexOf('|note:');
    if (pipeIdx !== -1) {
      return {
        orderedBy: noteStr.substring(11, pipeIdx).trim() || 'ไม่ระบุ',
        noteText: noteStr.substring(pipeIdx + 6).trim()
      };
    }
    return {
      orderedBy: noteStr.substring(11).trim() || 'ไม่ระบุ',
      noteText: ''
    };
  }
  return { orderedBy: 'ไม่ระบุ', noteText: typeof noteStr === 'string' ? noteStr.trim() : '' };
};

// Parser to convert raw logs and orders into standardized consumable dispense transactions
export const parseDispenseTransactions = (logs = [], orders = [], itemMaster = []) => {
  const transactions = [];

  // Helper to find matching master item
  const findMaster = (name, lot) => {
    if (!name) return null;
    const cleanName = name.trim().toLowerCase();

    // 1. Try matching both name and lot if lot provided
    if (lot && lot !== '-' && lot !== 'ไม่ระบุ') {
      const matchedLot = itemMaster.find(
        m => m.item_name && m.item_name.trim().toLowerCase() === cleanName && m.lot_number === lot
      );
      if (matchedLot) return matchedLot;
    }

    // 2. Try matching by name with price_per_unit > 0
    const withPrice = itemMaster.find(
      m => m.item_name && m.item_name.trim().toLowerCase() === cleanName && Number(m.price_per_unit) > 0
    );
    if (withPrice) return withPrice;

    // 3. Fallback to any matching name
    return itemMaster.find(m => m.item_name && m.item_name.trim().toLowerCase() === cleanName) || null;
  };

  // 1. Parse Logs
  (logs || []).forEach((log, index) => {
    const action = log.action || '';
    // Must contain "โอนย้าย" or "เบิกออก" or "จ่าย"
    if (!action.includes('โอนย้าย') && !action.includes('เบิกออก') && !action.includes('จ่าย')) {
      return;
    }

    const itemName = log.item_name || 'ไม่ระบุชื่อวัสดุ';

    // Quantity & Unit extraction: e.g. "จำนวน 5 กล่อง" or "จำนวน 12.5 หลอด"
    let quantity = 1;
    let unit = '';
    const qtyMatch = action.match(/จำนวน\s*([\d,.]+)\s*([^\s,()[\]]+)?/);
    if (qtyMatch) {
      const parsedQty = parseFloat(qtyMatch[1].replace(/,/g, ''));
      if (!isNaN(parsedQty) && parsedQty > 0) {
        quantity = parsedQty;
      }
      if (qtyMatch[2] && isNaN(qtyMatch[2])) {
        unit = qtyMatch[2].trim();
      }
    }

    // Lot Number extraction
    let lotNumber = '';
    const lotMatch = action.match(/\(Lot:\s*([^)]+)\)/i) || action.match(/Lot:\s*([^\s,()[\]]+)/i);
    if (lotMatch) {
      lotNumber = lotMatch[1].trim();
    } else {
      const codeMatch = action.match(/รหัส\s+([^\s,()[\]]+)/);
      if (codeMatch) {
        lotNumber = codeMatch[1].trim();
      }
    }

    // Route: From & To Location extraction
    let fromLocation = '';
    let toLocation = '';

    const routeMatch = action.match(/(?:โอนย้ายจาก|จาก)\s+(.+?)\s+ไปยัง\s+(.+?)(?:\s+จำนวน|\s+โดย|\s*\[|\s*\(|$)/);
    if (routeMatch) {
      fromLocation = routeMatch[1].trim();
      toLocation = routeMatch[2].trim();
    } else {
      const toMatch = action.match(/ไปยัง\s+(.+?)(?:\s+จำนวน|\s+โดย|\s*\[|\s*\(|$)/);
      if (toMatch) {
        toLocation = toMatch[1].trim();
      }
      const fromMatch = action.match(/(?:จาก)\s+(.+?)(?:\s+ไปยัง|\s+จำนวน|\s+โดย|\s*\[|\s*\(|$)/);
      if (fromMatch) {
        fromLocation = fromMatch[1].trim();
      }
    }

    if (!fromLocation) {
      if (action.includes('จ่ายออก') || action.includes('เบิกออก')) {
        fromLocation = 'คลังย่อย (คลังห้องฟัน)';
      } else if (action.includes('โอนย้ายสต็อกข้ามคลัง')) {
        fromLocation = 'คลังใหญ่ (คลังพัสดุ)';
      } else {
        fromLocation = 'คลังใหญ่ (คลังพัสดุ)';
      }
    }

    if (!toLocation) {
      toLocation = log.warehouse || 'ห้องคลีนิก';
    }

    // Actor extraction
    let actor = '';
    const actorMatch = action.match(/โดย\s+(.+?)(?:\s*\[|\s*\(|$)/);
    if (actorMatch) {
      actor = actorMatch[1].trim();
    } else {
      actor = log.actor || log.user || 'ไม่ระบุ';
    }

    // Note extraction
    let note = '';
    const noteMatch = action.match(/\[หมายเหตุ:\s*([^\]]+)\]/) || action.match(/หมายเหตุ:\s*(.+)$/);
    if (noteMatch) {
      note = noteMatch[1].trim();
    } else {
      note = log.note || '';
    }

    // Match with Item Master
    const master = findMaster(itemName, lotNumber);
    if (!lotNumber && master?.lot_number) {
      lotNumber = master.lot_number;
    }
    if (!unit) {
      unit = master?.unit || 'ชิ้น';
    }

    const unitPrice = Number(master?.price_per_unit) || 0;
    const specialtyInfo = resolveSpecialty(master?.specialty, itemName);
    const totalValue = quantity * unitPrice;

    transactions.push({
      id: log.id ? `log-${log.id}` : `log-${index}`,
      date: log.timestamp || log.created_at || new Date().toISOString(),
      itemName,
      lotNumber: lotNumber || '-',
      quantity,
      unit,
      unitPrice,
      totalValue,
      fromLocation,
      toLocation,
      actor,
      note,
      specialty: specialtyInfo.name,
      specialtyCode: specialtyInfo.code,
      specialtyColor: specialtyInfo.color,
      sourceType: 'log'
    });
  });

  // 2. Parse Completed Orders
  (orders || []).forEach((order, orderIdx) => {
    if (order.status !== 'completed') return;

    const date = order.timestamp || order.created_at || new Date().toISOString();
    const toLocation = order.clinic_name || 'ห้องคลีนิก';
    const fromLocation = 'คลังย่อย (คลังห้องฟัน)';

    const { orderedBy, noteText } = parseOrderNote(order.note);
    const orderItems = Array.isArray(order.items) ? order.items : [];

    orderItems.forEach((it, itIdx) => {
      const itemName = it.name || it.item_name || 'ไม่ระบุชื่อวัสดุ';
      const quantity = Number(it.qty || it.quantity || it.item_amount || 1);
      const lotNumber = it.lot_number || it.lot || '';

      const master = findMaster(itemName, lotNumber);
      const unit = it.unit || master?.unit || 'ชิ้น';
      const unitPrice = Number(it.price_per_unit || master?.price_per_unit || 0);
      const specialtyInfo = resolveSpecialty(master?.specialty, itemName);
      const totalValue = quantity * unitPrice;

      transactions.push({
        id: `order-${order.id || orderIdx}-${itIdx}`,
        date,
        itemName,
        lotNumber: lotNumber || master?.lot_number || '-',
        quantity,
        unit,
        unitPrice,
        totalValue,
        fromLocation,
        toLocation,
        actor: orderedBy !== 'ไม่ระบุ' ? orderedBy : (order.user || order.actor || 'ไม่ระบุ'),
        note: noteText,
        specialty: specialtyInfo.name,
        specialtyCode: specialtyInfo.code,
        specialtyColor: specialtyInfo.color,
        sourceType: 'order'
      });
    });
  });

  // Sort descending by date
  transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
  return transactions;
};

// Formatter Helpers
export const formatCurrency = (amount) => {
  return `${(Number(amount) || 0).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ฿`;
};

export const formatNumber = (num) => {
  return (Number(num) || 0).toLocaleString('th-TH');
};

export const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return isoString;
  }
};

const DispenseReportPage = ({
  supabase,
  itemMaster = [],
  supplies = [],
  onNavigate = () => {},
  user = null,
  isMobile = false,
  // Optional fallback data for testing/offline
  logs: propsLogs = null,
  orders: propsOrders = null,
  initialReportMode = 'summary',
  initialSelectedItemId = null
}) => {
  const [reportMode, setReportMode] = useState(initialReportMode || 'summary');
  const [selectedLedgerItem, setSelectedLedgerItem] = useState(() => {
    return (
      (initialSelectedItemId ? itemMaster.find(m => m.id === initialSelectedItemId) : null) ||
      itemMaster.find(m => m.category === 'วัสดุสิ้นเปลือง') ||
      itemMaster[0] ||
      null
    );
  });

  useEffect(() => {
    if (initialReportMode) {
      setReportMode(initialReportMode);
    }
  }, [initialReportMode]);

  useEffect(() => {
    if (initialSelectedItemId && itemMaster && itemMaster.length > 0) {
      const found = itemMaster.find(m => m.id === initialSelectedItemId);
      if (found) {
        setSelectedLedgerItem(found);
      }
    }
  }, [initialSelectedItemId, itemMaster]);

  useEffect(() => {
    if (!selectedLedgerItem && itemMaster && itemMaster.length > 0) {
      const found =
        (initialSelectedItemId ? itemMaster.find(m => m.id === initialSelectedItemId) : null) ||
        itemMaster.find(m => m.category === 'วัสดุสิ้นเปลือง') ||
        itemMaster[0] ||
        null;
      if (found) {
        setSelectedLedgerItem(found);
      }
    }
  }, [itemMaster, initialSelectedItemId, selectedLedgerItem]);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'destination' | 'specialty' | 'top-items'
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [rawLogs, setRawLogs] = useState([]);
  const [rawOrders, setRawOrders] = useState([]);

  // Generate Year Options (-4 to +1 years)
  const currentYear = now.getFullYear();
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y--) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  // Data Fetching Function
  const fetchReportData = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);

    const startOfMonth = new Date(selectedYear, selectedMonth - 1, 1, 0, 0, 0).toISOString();
    const endOfMonth = new Date(selectedYear, selectedMonth, 0, 23, 59, 59, 999).toISOString();

    let fetchedLogs = [];
    let fetchedOrders = [];

    if (supabase) {
      try {
        const { data: lData, error: lErr } = await supabase
          .from('logs')
          .select('*')
          .gte('timestamp', startOfMonth)
          .lte('timestamp', endOfMonth)
          .order('timestamp', { ascending: false });

        if (lErr) throw lErr;
        fetchedLogs = lData || [];
      } catch (err) {
        console.warn('DispenseReportPage: Error fetching logs:', err.message || err);
        setFetchError('ไม่สามารถเชื่อมต่อฐานข้อมูล Logs ได้ (ใช้ข้อมูลสำรองหากมี)');
        fetchedLogs = propsLogs || [];
      }

      try {
        const { data: oData, error: oErr } = await supabase
          .from('orders')
          .select('*')
          .gte('timestamp', startOfMonth)
          .lte('timestamp', endOfMonth);

        if (oErr) throw oErr;
        fetchedOrders = oData || [];
      } catch (err) {
        console.warn('DispenseReportPage: Error fetching orders:', err.message || err);
        fetchedOrders = propsOrders || [];
      }
    } else {
      // Graceful offline fallback
      fetchedLogs = propsLogs || [];
      fetchedOrders = propsOrders || [];
    }

    setRawLogs(fetchedLogs);
    setRawOrders(fetchedOrders);
    setIsLoading(false);
  }, [selectedYear, selectedMonth, supabase, propsLogs, propsOrders]);

  // Trigger fetch on month/year changes
  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Parse all transactions
  const transactions = useMemo(() => {
    return parseDispenseTransactions(rawLogs, rawOrders, itemMaster);
  }, [rawLogs, rawOrders, itemMaster]);

  // Filtered transactions for Tab 1
  const filteredTransactions = useMemo(() => {
    if (!searchQuery.trim()) return transactions;
    const q = searchQuery.trim().toLowerCase();
    return transactions.filter(tx =>
      tx.itemName.toLowerCase().includes(q) ||
      tx.toLocation.toLowerCase().includes(q) ||
      tx.fromLocation.toLowerCase().includes(q) ||
      tx.actor.toLowerCase().includes(q) ||
      tx.lotNumber.toLowerCase().includes(q) ||
      tx.specialty.toLowerCase().includes(q) ||
      tx.note.toLowerCase().includes(q)
    );
  }, [transactions, searchQuery]);

  // Aggregated Destination Summary
  const destinationSummary = useMemo(() => {
    const map = {};
    transactions.forEach(tx => {
      const loc = (tx.toLocation || 'ไม่ระบุ').replace(/^[\s-]+/, '').trim();
      if (!map[loc]) {
        map[loc] = {
          name: loc,
          quantity: 0,
          value: 0,
          count: 0,
          items: new Set()
        };
      }
      map[loc].quantity += tx.quantity;
      map[loc].value += tx.totalValue;
      map[loc].count += 1;
      map[loc].items.add(tx.itemName);
    });

    const totalVal = transactions.reduce((sum, t) => sum + t.totalValue, 0);

    return Object.values(map)
      .map(d => ({
        ...d,
        itemCount: d.items.size,
        percent: totalVal > 0 ? (d.value / totalVal) * 100 : 0
      }))
      .sort((a, b) => b.value - a.value || b.quantity - a.quantity);
  }, [transactions]);

  // Aggregated Dental Specialty Summary (All 8 Specialties)
  const specialtySummary = useMemo(() => {
    const map = {};
    DENTAL_SPECIALTIES.forEach(s => {
      map[s.name] = {
        id: s.id,
        name: s.name,
        code: s.code,
        color: s.color,
        quantity: 0,
        value: 0,
        count: 0,
        items: new Set()
      };
    });

    transactions.forEach(tx => {
      const sName = tx.specialty || 'ทันตกรรมทั่วไป/อื่นๆ';
      if (!map[sName]) {
        map[sName] = {
          id: tx.specialtyCode || 'GEN',
          name: sName,
          code: tx.specialtyCode || 'GEN',
          color: tx.specialtyColor || '#4b5563',
          quantity: 0,
          value: 0,
          count: 0,
          items: new Set()
        };
      }
      map[sName].quantity += tx.quantity;
      map[sName].value += tx.totalValue;
      map[sName].count += 1;
      map[sName].items.add(tx.itemName);
    });

    const totalVal = transactions.reduce((sum, t) => sum + t.totalValue, 0);

    return Object.values(map)
      .map(s => ({
        ...s,
        itemCount: s.items.size,
        percent: totalVal > 0 ? (s.value / totalVal) * 100 : 0
      }))
      .sort((a, b) => b.value - a.value || b.quantity - a.quantity);
  }, [transactions]);

  // Aggregated Top 10 Fast-Moving Consumables
  const topItemsSummary = useMemo(() => {
    const map = {};
    transactions.forEach(tx => {
      if (!map[tx.itemName]) {
        map[tx.itemName] = {
          itemName: tx.itemName,
          quantity: 0,
          value: 0,
          count: 0,
          unit: tx.unit,
          specialty: tx.specialty,
          specialtyCode: tx.specialtyCode,
          specialtyColor: tx.specialtyColor
        };
      }
      map[tx.itemName].quantity += tx.quantity;
      map[tx.itemName].value += tx.totalValue;
      map[tx.itemName].count += 1;
    });

    const sorted = Object.values(map).sort((a, b) => b.quantity - a.quantity || b.value - a.value);
    const maxQty = sorted[0]?.quantity || 1;

    return sorted.slice(0, 10).map((it, idx) => ({
      ...it,
      rank: idx + 1,
      relativePercent: maxQty > 0 ? (it.quantity / maxQty) * 100 : 0
    }));
  }, [transactions]);

  // Overall KPIs
  const totalMonthlyValue = useMemo(() => {
    return transactions.reduce((sum, tx) => sum + tx.totalValue, 0);
  }, [transactions]);

  const totalMonthlyQuantity = useMemo(() => {
    return transactions.reduce((sum, tx) => sum + tx.quantity, 0);
  }, [transactions]);

  const topDestination = useMemo(() => {
    if (destinationSummary.length === 0) return '-';
    const top = destinationSummary[0];
    return `${top.name} (${formatNumber(top.quantity)} ชิ้น)`;
  }, [destinationSummary]);

  const topSpecialty = useMemo(() => {
    const activeSpecialties = specialtySummary.filter(s => s.quantity > 0);
    if (activeSpecialties.length === 0) return '-';
    const top = activeSpecialties[0];
    return `${top.name} (${formatCurrency(top.value)})`;
  }, [specialtySummary]);

  // Quick Date Selectors
  const handleCurrentMonth = () => {
    const d = new Date();
    setSelectedMonth(d.getMonth() + 1);
    setSelectedYear(d.getFullYear());
  };

  const handlePrevMonth = () => {
    setSelectedMonth(prev => {
      if (prev === 1) {
        setSelectedYear(y => y - 1);
        return 12;
      }
      return prev - 1;
    });
  };

  const handleThreeMonthsAgo = () => {
    const target = new Date(selectedYear, selectedMonth - 1 - 3, 1);
    setSelectedMonth(target.getMonth() + 1);
    setSelectedYear(target.getFullYear());
  };

  const currentMonthName = THAI_MONTHS.find(m => m.value === selectedMonth)?.name || `เดือน ${selectedMonth}`;
  const thaiYear = selectedYear + 543;

  // CSV Export Handler
  const handleExportCSV = () => {
    if (transactions.length === 0) {
      alert('ไม่มีข้อมูลการเบิกจ่ายในเดือนที่เลือกเพื่อ Export');
      return;
    }

    const headers = [
      'ลำดับ',
      'วันที่-เวลา',
      'ชื่อวัสดุ',
      'Lot',
      'จำนวน',
      'หน่วย',
      'ราคาต่อหน่วย(บาท)',
      'มูลค่ารวม(บาท)',
      'ต้นทาง',
      'ปลายทาง',
      'ผู้เบิก/ทำรายการ',
      'สาขาทันตกรรม',
      'หมายเหตุ'
    ];

    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const rows = transactions.map((tx, idx) => [
      idx + 1,
      formatDateTime(tx.date),
      tx.itemName,
      tx.lotNumber,
      tx.quantity,
      tx.unit,
      tx.unitPrice.toFixed(2),
      tx.totalValue.toFixed(2),
      tx.fromLocation,
      tx.toLocation,
      tx.actor,
      tx.specialty,
      tx.note
    ].map(escapeCsv).join(','));

    // UTF-8 BOM \uFEFF ensures perfect Thai language rendering in MS Excel
    const csvContent = '\uFEFF' + [headers.map(escapeCsv).join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `dental_dispense_report_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Print Report Handler
  const handlePrintReport = () => {
    window.print();
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '24px', maxWidth: '1400px', margin: '0 auto', fontFamily: 'sans-serif' }}>
      {/* Styles for Screen & Print */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: #fff !important;
            color: #000 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print-only {
            display: block !important;
          }
          .report-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 11px !important;
          }
          .report-table th, .report-table td {
            border: 1px solid #333 !important;
            padding: 4px 6px !important;
            color: #000 !important;
          }
          .report-card {
            box-shadow: none !important;
            border: 1px solid #ccc !important;
            page-break-inside: avoid;
          }
        }
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>

      {/* Top Mode Switcher Banner */}
      <div className="no-print" style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '20px',
        background: '#f8fafc',
        padding: '6px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          type="button"
          onClick={() => setReportMode('summary')}
          style={{
            flex: 1,
            minWidth: '280px',
            padding: '12px 18px',
            borderRadius: '8px',
            border: 'none',
            background: reportMode === 'summary' ? '#2563eb' : 'transparent',
            color: reportMode === 'summary' ? '#ffffff' : '#475569',
            fontWeight: reportMode === 'summary' ? '700' : '500',
            fontSize: '15px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: reportMode === 'summary' ? '0 2px 8px rgba(37, 99, 235, 0.25)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          <span>📊 1. รายงานสรุปภาพรวมรายเดือน (Monthly Summary Dashboard)</span>
        </button>
        <button
          type="button"
          onClick={() => setReportMode('ledger')}
          style={{
            flex: 1,
            minWidth: '280px',
            padding: '12px 18px',
            borderRadius: '8px',
            border: 'none',
            background: reportMode === 'ledger' ? '#059669' : 'transparent',
            color: reportMode === 'ledger' ? '#ffffff' : '#475569',
            fontWeight: reportMode === 'ledger' ? '700' : '500',
            fontSize: '15px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: reportMode === 'ledger' ? '0 2px 8px rgba(5, 150, 105, 0.25)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          <span>📑 2. บัญชีวัสดุ (แบบฟอร์มทางการตามระเบียบพัสดุ - Material Ledger Form)</span>
        </button>
      </div>

      {reportMode === 'ledger' ? (
        <div>
          <div className="no-print" style={{ marginBottom: '14px' }}>
            <button
              type="button"
              onClick={() => onNavigate('consumable-dashboard')}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#fff',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              ← แดชบอร์ด
            </button>
          </div>
          <MaterialLedgerSheet
            item={selectedLedgerItem}
            itemMaster={itemMaster}
            onSelectItem={setSelectedLedgerItem}
            supabase={supabase}
            supplies={supplies}
            user={user}
          />
        </div>
      ) : (
        <>
          {/* Formal Printable Header (Visible when Printing) */}
          <div className="print-only" style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 6px 0', fontSize: '20px', fontWeight: 'bold' }}>
          โรงพยาบาลฝาง - กลุ่มงานทันตกรรม
        </h2>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 'normal' }}>
          รายงานสรุปการเบิกจ่ายเวชภัณฑ์และวัสดุสิ้นเปลือง ประจำเดือน {currentMonthName} พ.ศ. {thaiYear}
        </h3>
        <div style={{ fontSize: '12px', color: '#555', marginBottom: '16px' }}>
          ข้อมูล ณ วันที่ {new Date().toLocaleDateString('th-TH')} | มูลค่าการเบิกจ่ายรวม {formatCurrency(totalMonthlyValue)} ({formatNumber(totalMonthlyQuantity)} ชิ้น)
        </div>
      </div>

      {/* Top Header & Actions (Screen View) */}
      <div className="no-print" style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => onNavigate('consumable-dashboard')}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#fff',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            ← แดชบอร์ด
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#0f172a' }}>
              📊 รายงานสรุปการเบิกจ่ายเวชภัณฑ์
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
              รายงานวิเคราะห์มูลค่าและปริมาณการเบิกจ่าย ประจำเดือน {currentMonthName} พ.ศ. {thaiYear}
            </p>
          </div>
        </div>

        {/* Action Buttons: CSV & Print */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={handleExportCSV}
            style={{
              padding: '9px 16px',
              borderRadius: '8px',
              border: '1px solid #16a34a',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 4px rgba(22,163,74,0.2)'
            }}
          >
            📥 ส่งออก CSV (Excel)
          </button>
          <button
            onClick={handlePrintReport}
            style={{
              padding: '9px 16px',
              borderRadius: '8px',
              border: '1px solid #0284c7',
              background: '#0284c7',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 4px rgba(2,132,199,0.2)'
            }}
          >
            🖨️ พิมพ์รายงาน / PDF
          </button>
        </div>
      </div>

      {/* Month & Year Filter Bar */}
      <div className="no-print" style={{
        background: '#ffffff',
        padding: '16px 20px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px'
      }}>
        {/* Dropdowns */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#334155' }}>📅 เลือกเดือน:</span>
            <select
              aria-label="เลือกเดือน"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                fontWeight: '500',
                background: '#f8fafc',
                cursor: 'pointer'
              }}
            >
              {THAI_MONTHS.map(m => (
                <option key={m.value} value={m.value}>
                  {m.name} ({m.value})
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#334155' }}>ปี:</span>
            <select
              aria-label="เลือกปี"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                fontWeight: '500',
                background: '#f8fafc',
                cursor: 'pointer'
              }}
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>
                  พ.ศ. {y + 543} (ค.ศ. {y})
                </option>
              ))}
            </select>
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchReportData}
            disabled={isLoading}
            title="รีเฟรชข้อมูล"
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#f1f5f9',
              fontSize: '14px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🔄 {isLoading ? 'กำลังโหลด...' : 'รีเฟรช'}
          </button>
        </div>

        {/* Quick Range Selection Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>ตัวเลือกด่วน:</span>
          <button
            onClick={handleCurrentMonth}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: '#fff',
              fontSize: '13px',
              cursor: 'pointer',
              color: '#0284c7',
              fontWeight: '500'
            }}
          >
            เดือนนี้ (Current Month)
          </button>
          <button
            onClick={handlePrevMonth}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: '#fff',
              fontSize: '13px',
              cursor: 'pointer',
              color: '#475569',
              fontWeight: '500'
            }}
          >
            เดือนที่แล้ว (Previous Month)
          </button>
          <button
            onClick={handleThreeMonthsAgo}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: '#fff',
              fontSize: '13px',
              cursor: 'pointer',
              color: '#475569',
              fontWeight: '500'
            }}
          >
            ย้อนหลัง 3 เดือน
          </button>
        </div>
      </div>

      {fetchError && (
        <div style={{
          padding: '12px 16px',
          background: '#fffbeb',
          border: '1px solid #fef3c7',
          color: '#b45309',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          ⚠️ {fetchError}
        </div>
      )}

      {/* Top KPI Cards (Monthly Summary) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* KPI 1: Total Value */}
        <div className="report-card" style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          borderLeft: '5px solid #0284c7'
        }}>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '4px' }}>
            💰 มูลค่าการเบิกจ่ายรวม (฿)
          </div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#0284c7' }}>
            {formatCurrency(totalMonthlyValue)}
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            ประจำ {currentMonthName} {thaiYear}
          </div>
        </div>

        {/* KPI 2: Total Quantity */}
        <div className="report-card" style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          borderLeft: '5px solid #16a34a'
        }}>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '4px' }}>
            📦 ปริมาณชิ้นที่เบิกจ่ายรวม
          </div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#16a34a' }}>
            {formatNumber(totalMonthlyQuantity)} ชิ้น
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            ยอดรวมทุกรายการเวชภัณฑ์
          </div>
        </div>

        {/* KPI 3: Total Transactions */}
        <div className="report-card" style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          borderLeft: '5px solid #8b5cf6'
        }}>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '4px' }}>
            📋 จำนวนรอบทำรายการ
          </div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#8b5cf6' }}>
            {formatNumber(transactions.length)} ครั้ง
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            Logs และ Orders ที่เสร็จสมบูรณ์
          </div>
        </div>

        {/* KPI 4: Top Destination */}
        <div className="report-card" style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          borderLeft: '5px solid #ea580c'
        }}>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '4px' }}>
            🏥 ห้องตรวจที่เบิกสูงสุด
          </div>
          <div style={{
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#ea580c',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {topDestination}
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            ปลายทางที่มีการเบิกจ่ายมากที่สุด
          </div>
        </div>

        {/* KPI 5: Top Specialty */}
        <div className="report-card" style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          borderLeft: '5px solid #0d9488'
        }}>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '4px' }}>
            🦷 สาขาทันตกรรมที่ใช้สูงสุด
          </div>
          <div style={{
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#0d9488',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {topSpecialty}
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            สาขาที่มีมูลค่าการใช้วัสดุสูงสุด
          </div>
        </div>
      </div>

      {/* Tabs Navigation (Screen View) */}
      <div className="no-print" style={{
        display: 'flex',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '20px',
        overflowX: 'auto',
        gap: '4px'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '12px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'all' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'all' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'all' ? 'bold' : '500',
            fontSize: '14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          📋 รายการเบิกจ่ายทั้งหมด ({transactions.length})
        </button>
        <button
          onClick={() => setActiveTab('destination')}
          style={{
            padding: '12px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'destination' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'destination' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'destination' ? 'bold' : '500',
            fontSize: '14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          🏥 แยกตามห้องตรวจ/ปลายทาง ({destinationSummary.length})
        </button>
        <button
          onClick={() => setActiveTab('specialty')}
          style={{
            padding: '12px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'specialty' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'specialty' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'specialty' ? 'bold' : '500',
            fontSize: '14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          🦷 แยกตามสาขาทันตกรรม (8 สาขา)
        </button>
        <button
          onClick={() => setActiveTab('top-items')}
          style={{
            padding: '12px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'top-items' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'top-items' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'top-items' ? 'bold' : '500',
            fontSize: '14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          🏆 10 อันดับวัสดุเบิกใช้สูงสุด
        </button>
      </div>

      {/* TAB CONTENT */}

      {/* Tab 1: All Transactions */}
      {activeTab === 'all' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          {/* Search Box */}
          <div className="no-print" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <input
              type="text"
              placeholder="🔍 ค้นหาชื่อวัสดุ, ห้องปลายทาง, ผู้ทำรายการ, หรือ Lot..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '9px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                width: isMobile ? '100%' : '380px'
              }}
            />
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              แสดง {filteredTransactions.length} จากทั้งหมด {transactions.length} รายการ
            </div>
          </div>

          {filteredTransactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>📂</div>
              <div style={{ fontSize: '15px' }}>ไม่พบรายการเบิกจ่ายในรอบเดือนนี้</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '10px', width: '40px', textAlign: 'center' }}>ลำดับ</th>
                    <th style={{ padding: '10px' }}>วันที่-เวลา</th>
                    <th style={{ padding: '10px' }}>ชื่อวัสดุสิ้นเปลือง</th>
                    <th style={{ padding: '10px' }}>Lot</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>จำนวน</th>
                    <th style={{ padding: '10px' }}>หน่วย</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>ราคา/หน่วย</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>มูลค่ารวม</th>
                    <th style={{ padding: '10px' }}>เส้นทาง (ต้นทาง ➔ ปลายทาง)</th>
                    <th style={{ padding: '10px' }}>ผู้ทำรายการ</th>
                    <th style={{ padding: '10px' }}>หมายเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((tx, idx) => (
                    <tr
                      key={tx.id}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: idx % 2 === 0 ? '#ffffff' : '#fcfdfd'
                      }}
                    >
                      <td style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '10px', whiteSpace: 'nowrap', color: '#475569' }}>
                        {formatDateTime(tx.date)}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>{tx.itemName}</div>
                        <span style={{
                          display: 'inline-block',
                          fontSize: '11px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: `${tx.specialtyColor}15`,
                          color: tx.specialtyColor,
                          fontWeight: '600',
                          marginTop: '2px'
                        }}>
                          {tx.specialty}
                        </span>
                      </td>
                      <td style={{ padding: '10px', color: '#64748b' }}>
                        <code>{tx.lotNumber}</code>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>
                        {formatNumber(tx.quantity)}
                      </td>
                      <td style={{ padding: '10px', color: '#64748b' }}>
                        {tx.unit}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#475569' }}>
                        {tx.unitPrice > 0 ? formatCurrency(tx.unitPrice) : '-'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#0284c7' }}>
                        {formatCurrency(tx.totalValue)}
                      </td>
                      <td style={{ padding: '10px', color: '#334155' }}>
                        <span style={{ color: '#64748b' }}>{tx.fromLocation}</span>
                        {' ➔ '}
                        <span style={{ fontWeight: '600', color: '#0f172a' }}>{tx.toLocation}</span>
                      </td>
                      <td style={{ padding: '10px', color: '#475569' }}>
                        {tx.actor}
                      </td>
                      <td style={{ padding: '10px', color: '#64748b', fontSize: '12px' }}>
                        {tx.note || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Destination Summary */}
      {activeTab === 'destination' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e293b' }}>
            🏥 สรุปยอดการเบิกจ่ายแยกตามห้องตรวจ / คลังปลายทาง
          </h3>

          {destinationSummary.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
              ไม่พบข้อมูลการเบิกจ่ายในรอบเดือนนี้
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '10px', width: '40px', textAlign: 'center' }}>#</th>
                    <th style={{ padding: '10px' }}>ห้องตรวจ / คลังปลายทาง</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>จำนวนชนิดวัสดุ</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>ปริมาณชิ้นรวม</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>มูลค่ารวม (฿)</th>
                    <th style={{ padding: '10px', width: '250px' }}>สัดส่วนมูลค่า (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {destinationSummary.map((d, idx) => (
                    <tr key={d.name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>{idx + 1}</td>
                      <td style={{ padding: '10px', fontWeight: 'bold', color: '#1e293b' }}>
                        {d.name}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#475569' }}>
                        {formatNumber(d.itemCount)} ชนิด
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#0f172a' }}>
                        {formatNumber(d.quantity)} ชิ้น
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#0284c7' }}>
                        {formatCurrency(d.value)}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            flex: 1,
                            height: '8px',
                            background: '#e2e8f0',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${d.percent}%`,
                              height: '100%',
                              background: '#0284c7',
                              borderRadius: '4px'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: '600', width: '45px', textAlign: 'right', color: '#64748b' }}>
                            {d.percent.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Dental Specialty Summary */}
      {activeTab === 'specialty' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e293b' }}>
            🦷 สรุปยอดการเบิกจ่ายแยกตาม 8 สาขาทันตกรรม
          </h3>

          <div style={{ overflowX: 'auto' }}>
            <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '10px', width: '50px', textAlign: 'center' }}>รหัส</th>
                  <th style={{ padding: '10px' }}>สาขาทันตกรรม</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>จำนวนชนิดวัสดุ</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>ปริมาณชิ้นรวม</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>มูลค่ารวม (฿)</th>
                  <th style={{ padding: '10px', width: '250px' }}>สัดส่วนมูลค่า (%)</th>
                </tr>
              </thead>
              <tbody>
                {specialtySummary.map((s) => (
                  <tr key={s.id || s.name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: `${s.color}20`,
                        color: s.color,
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }}>
                        {s.code}
                      </span>
                    </td>
                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#1e293b' }}>
                      {s.name}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#475569' }}>
                      {formatNumber(s.itemCount)} ชนิด
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#0f172a' }}>
                      {formatNumber(s.quantity)} ชิ้น
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: s.color }}>
                      {formatCurrency(s.value)}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          flex: 1,
                          height: '8px',
                          background: '#e2e8f0',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${s.percent}%`,
                            height: '100%',
                            background: s.color,
                            borderRadius: '4px'
                          }} />
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: '600', width: '45px', textAlign: 'right', color: '#64748b' }}>
                          {s.percent.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 4: Top 10 Fast-Moving Consumables */}
      {activeTab === 'top-items' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e293b' }}>
            🏆 10 อันดับวัสดุสิ้นเปลืองที่มีการเบิกใช้สูงสุด
          </h3>

          {topItemsSummary.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
              ไม่พบข้อมูลการเบิกจ่ายในรอบเดือนนี้
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {topItemsSummary.map((it) => {
                const badgeMedal =
                  it.rank === 1 ? '🥇' :
                  it.rank === 2 ? '🥈' :
                  it.rank === 3 ? '🥉' : `#${it.rank}`;

                return (
                  <div
                    key={it.itemName}
                    style={{
                      padding: '14px 18px',
                      borderRadius: '10px',
                      background: it.rank <= 3 ? '#f8fafc' : '#ffffff',
                      border: it.rank <= 3 ? '1px solid #cbd5e1' : '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      flexWrap: 'wrap'
                    }}
                  >
                    {/* Rank Badge */}
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      width: '36px',
                      textAlign: 'center',
                      color: it.rank === 1 ? '#d97706' : it.rank === 2 ? '#64748b' : it.rank === 3 ? '#b45309' : '#94a3b8'
                    }}>
                      {badgeMedal}
                    </div>

                    {/* Item Details */}
                    <div style={{ flex: '1 1 240px' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#0f172a' }}>
                        {it.itemName}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: `${it.specialtyColor}15`,
                          color: it.specialtyColor,
                          fontWeight: '600'
                        }}>
                          {it.specialty}
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          เบิกทั้งหมด {formatNumber(it.count)} ครั้ง
                        </span>
                      </div>
                    </div>

                    {/* Progress Bar & Amount */}
                    <div style={{ flex: '1 1 200px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '13px',
                        marginBottom: '4px'
                      }}>
                        <span style={{ fontWeight: 'bold', color: '#0f172a' }}>
                          {formatNumber(it.quantity)} {it.unit}
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#0284c7' }}>
                          {formatCurrency(it.value)}
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        background: '#e2e8f0',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${it.relativePercent}%`,
                          height: '100%',
                          background: it.rank === 1 ? '#eab308' : '#0284c7',
                          borderRadius: '4px'
                        }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Formal Signature Block (Print View & Formal Verification) */}
      <div className="print-only" style={{ marginTop: '60px', pageBreakInside: 'avoid' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 40px' }}>
          <div style={{ textAlign: 'center', width: '300px' }}>
            <div style={{ marginBottom: '50px' }}>
              (ลงชื่อ) ............................................................ ผู้จัดทำรายงาน
            </div>
            <div>
              (............................................................)
            </div>
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
              ตำแหน่ง ............................................................
            </div>
          </div>

          <div style={{ textAlign: 'center', width: '300px' }}>
            <div style={{ marginBottom: '50px' }}>
              (ลงชื่อ) ............................................................ ผู้ตรวจสอบ / หัวหน้ากลุ่มงานทันตกรรม
            </div>
            <div>
              (............................................................)
            </div>
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
              ตำแหน่ง ............................................................
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default DispenseReportPage;
